import type { AgentMemoryMetrics } from '@cortex/types';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import '../../../components/refinement/refinement.css';
import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Sparkline } from '../../../components/common/Sparkline';
import { Spinner } from '../../../components/common/Spinner';
import { WindowPicker, type WindowDays } from '../../../components/common/WindowPicker';
import { useMemoryMetrics } from '../../../lib/agent-memory';
import { routes } from '../../../lib/constants';
import { formatDate } from '../../../lib/format';

/**
 * /agents/memories/metrics — memory lifecycle operations metrics
 * (DEV-16621), against the unified /v1/memories/metrics API (DEV-16962,
 * DEV-16963). Lives under the Agents folder's Memories sub-page.
 */
function MemoryMetricsPage() {
  const [days, setDays] = useState<WindowDays>(30);
  const q = useMemoryMetrics(days);

  return (
    <div className="refinement-memory-metrics">
      <PageHeader
        title="Memory operations metrics"
        actions={
          <>
            <BackButton to={routes.MEMORY} label="Memories" />
            <WindowPicker value={days} onChange={setDays} />
          </>
        }
      />

      {q.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-6)' }}>
          <Spinner />
        </div>
      ) : q.isError ? (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)' }}>
          Failed to load metrics: {q.error instanceof Error ? q.error.message : 'unknown error'}
        </div>
      ) : q.data ? (
        q.data.total === 0 ? (
          <EmptyState
            title="No memories yet"
            description="Once the agent proposes its first memory (or an operator authors one manually) the metrics here will start filling in."
          />
        ) : (
          <MetricsBody data={q.data} />
        )
      ) : null}
    </div>
  );
}

function MetricsBody({ data }: { data: AgentMemoryMetrics }) {
  const ratePct = data.approvalRate.rate === null ? null : Math.round(data.approvalRate.rate * 100);
  const queueBackingUp = data.pendingQueue.olderThan7dCount > 0;
  const activeCorpus = data.byStatus.pending + data.byStatus.candidate + data.byStatus.approved;

  return (
    <div className="refinement-memory-metrics__body">
      {queueBackingUp ? (
        <div className="alert alert--warning refinement-memory-metrics__alert">
          <strong>Pending queue is backing up.</strong> {data.pendingQueue.olderThan7dCount} pending
          memor{data.pendingQueue.olderThan7dCount === 1 ? 'y has' : 'ies have'} been awaiting
          review for more than 7 days.{' '}
          <Link to="/agents/memories" search={{ status: 'pending' }}>
            Review pending
          </Link>
        </div>
      ) : null}

      <section className="refinement-memory-metrics__row">
        <Card label="Active corpus" value={activeCorpus}>
          <span className="t-xs t-muted">
            {data.byStatus.approved} approved · {data.byStatus.pending + data.byStatus.candidate}{' '}
            pending
          </span>
        </Card>
        <Card
          label={`Approval rate (${data.approvalRate.windowDays}d)`}
          value={ratePct === null ? '—' : `${ratePct}%`}
        >
          <span className="t-xs t-muted">
            {data.approvalRate.approved}/
            {data.approvalRate.approved +
              data.approvalRate.rejected +
              data.approvalRate.pending +
              data.approvalRate.candidate}{' '}
            in window
          </span>
        </Card>
        <Card label="Rejected · archived" value={data.byStatus.rejected + data.archived}>
          <span className="t-xs t-muted">
            {data.byStatus.rejected} rejected · {data.archived} archived
          </span>
        </Card>
        <Card label="Total ever" value={data.total}>
          <span className="t-xs t-muted">across the org corpus</span>
        </Card>
      </section>

      <section className="refinement-memory-metrics__card refinement-memory-metrics__sparkline">
        <header>
          <h3 className="t-mono-label">Saves per week</h3>
          <span className="t-xs t-muted">{data.savesPerWeek.length} weeks</span>
        </header>
        <Sparkline
          values={data.savesPerWeek.map((b) => b.count)}
          width={520}
          height={56}
          ariaLabel="Memory saves per ISO week"
          style={{ width: '100%' }}
        />
        <footer className="t-xs t-muted">
          {data.savesPerWeek.length > 0
            ? `Latest week: ${data.savesPerWeek[data.savesPerWeek.length - 1]?.count ?? 0} save${
                (data.savesPerWeek[data.savesPerWeek.length - 1]?.count ?? 0) === 1 ? '' : 's'
              }`
            : 'No data'}
        </footer>
      </section>

      <section className="refinement-memory-metrics__row">
        <DistroCard
          label="Source"
          rows={[
            { name: 'agent-proposed', count: data.sourceSplit.agent },
            { name: 'human-authored', count: data.sourceSplit.manual },
          ]}
        />
        <DistroCard
          label="Scope"
          rows={[
            { name: 'org-wide', count: data.scopeDistribution.appliesToAll },
            { name: 'agent-bound', count: data.scopeDistribution.agentBound },
            { name: 'subject-scoped', count: data.scopeDistribution.subjectScoped },
            // `?? 0`: an API pod older than the user axis omits the field —
            // NaN here renders EVERY distro row as 0% (rolling-deploy skew).
            { name: 'single-user', count: data.scopeDistribution.userScoped ?? 0 },
          ]}
        />
        <PendingAgeCard data={data} />
        <ExactDuplicatesCard data={data} />
      </section>

      {data.byAgent.length > 0 ? (
        <section className="refinement-memory-metrics__card">
          <header>
            <h3 className="t-mono-label">By agent</h3>
            <span className="t-xs t-muted">{data.byAgent.length} agents</span>
          </header>
          <table className="refinement-memory-metrics__table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Pending</th>
                <th>Approved</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.byAgent.map((a) => (
                <tr key={a.agentId}>
                  <td>
                    <code className="t-mono t-xs">{a.agentId}</code>
                  </td>
                  <td>{a.pending > 0 ? <strong>{a.pending}</strong> : a.pending}</td>
                  <td>{a.approved}</td>
                  <td>
                    {a.pending > 0 ? (
                      <Link
                        to="/agents/memories"
                        search={{ status: 'pending', agentId: a.agentId }}
                        className="btn btn--ghost btn--xs"
                      >
                        Review
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {data.recentlyArchived.length > 0 ? (
        <section className="refinement-memory-metrics__card">
          <header>
            <h3 className="t-mono-label">Recently archived</h3>
            <span className="t-xs t-muted">latest {data.recentlyArchived.length}</span>
          </header>
          <ul className="refinement-memory-metrics__archived-list">
            {data.recentlyArchived.map((m) => (
              <li key={m.id}>
                <span className="badge badge--neutral">{m.type.toLowerCase()}</span>
                <span className="refinement-memory-metrics__archived-content">{m.content}</span>
                <span className="t-xs t-muted">{formatDate(m.archivedAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function Card({
  label,
  value,
  children,
}: {
  label: string;
  value: string | number;
  children?: ReactNode;
}) {
  return (
    <div className="refinement-memory-metrics__stat">
      <span className="t-mono-label">{label}</span>
      <span className="refinement-memory-metrics__stat-value">{value}</span>
      {children}
    </div>
  );
}

function DistroCard({
  label,
  rows,
}: {
  label: string;
  rows: ReadonlyArray<{ name: string; count: number }>;
}) {
  const total = rows.reduce((a, b) => a + b.count, 0);
  return (
    <div className="refinement-memory-metrics__card refinement-memory-metrics__distro">
      <header>
        <h3 className="t-mono-label">{label}</h3>
        <span className="t-xs t-muted">{total} total</span>
      </header>
      <ul>
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <li key={r.name}>
              <span>{r.name}</span>
              <span className="refinement-memory-metrics__distro-bar" aria-hidden="true">
                <span style={{ width: `${pct}%` }} />
              </span>
              <span className="t-xs t-muted">
                {r.count} · {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PendingAgeCard({ data }: { data: AgentMemoryMetrics }) {
  const ageDays = data.pendingQueue.oldestAgeDays;
  return (
    <div className="refinement-memory-metrics__card refinement-memory-metrics__distro">
      <header>
        <h3 className="t-mono-label">Pending queue</h3>
      </header>
      <ul>
        <li>
          <span>Oldest pending</span>
          <span className="t-xs t-muted">{ageDays === null ? 'none' : `${ageDays}d ago`}</span>
        </li>
        <li>
          <span>Older than 7 days</span>
          <span className="t-xs">
            {data.pendingQueue.olderThan7dCount > 0 ? (
              <strong style={{ color: 'var(--status-warning)' }}>
                {data.pendingQueue.olderThan7dCount}
              </strong>
            ) : (
              <span className="t-muted">0</span>
            )}
          </span>
        </li>
      </ul>
    </div>
  );
}

/**
 * The pilot's duplicate-rate dial: L1 exact-dedup pressure. This is the
 * evidence the v3 memory-quality gates read — a climbing skip count says the
 * semantic (L2) / judge (L3) layers are worth building; a flat one says the
 * deterministic layer is enough.
 */
function ExactDuplicatesCard({ data }: { data: AgentMemoryMetrics }) {
  // Rolling-deploy skew guard: an API pod that predates this field returns no
  // `exactDuplicates` key (useMemoryMetrics casts rather than parses) — the
  // card must degrade to zeros, not TypeError the whole metrics page.
  const d = data.exactDuplicates ?? { totalSkips: 0, memoriesAffected: 0, skipsInWindow: 0 };
  return (
    <div className="refinement-memory-metrics__card refinement-memory-metrics__distro">
      <header>
        <h3 className="t-mono-label">Exact duplicates (L1)</h3>
      </header>
      <ul>
        <li>
          <span>Skipped saves</span>
          <span className="t-xs">
            {d.totalSkips > 0 ? (
              <strong>{d.totalSkips}</strong>
            ) : (
              <span className="t-muted">0</span>
            )}
          </span>
        </li>
        <li>
          <span>Memories affected</span>
          <span className="t-xs t-muted">{d.memoriesAffected}</span>
        </li>
        <li>
          <span>Hit in window ({data.windowDays}d)</span>
          <span className="t-xs t-muted">{d.skipsInWindow}</span>
        </li>
      </ul>
    </div>
  );
}

export const Route = createFileRoute('/agents/memories/metrics')({
  component: MemoryMetricsPage,
});
